import { type RefObject, useState, useLayoutEffect } from 'react';
import { getBBox } from './isEveryPropertyDefined.tsx';

/** Provides a **stateful** value of the physical width of an SVG element _after
 *  being_ rendered by the DOM initially as well as _after changes to dependancies_ provided.
 *
 *  This is most often used when dynamic width from things like text inside
 *  an SVG element needs to be wrapped (eg Statuses)
 *
 * @example
 * const labelWidth = useLayoutWidth(textRef, [label, icons, starts, end]);
 */
export const useLayoutWidth = <T extends SVGGraphicsElement>(
	ref: RefObject<T>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dependancies: any[] = [],
): number => {
	const [width, setWidth] = useState(0);

	useLayoutEffect(() => {
		if (!ref.current) {
			setWidth(0);
			return;
		}

		const bBox = getBBox(ref.current);

		if (!bBox) {
			setWidth(0);
			return;
		}

		setWidth(Math.max(0, bBox.width));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref, ...dependancies]);

	return width;
};

export default useLayoutWidth;
